import { useQuery } from "@tanstack/react-query";
import { fetcher } from "@/lib/api";
import { useUserStore } from "@/store/useUserStore";

export interface SearchMetaItem {
  key: string;
  label: string;
}

interface SearchMetaResponse {
  success: boolean;
  role: string;
  items: SearchMetaItem[];
}

/**
 * Fetches the list of search groups the current role is allowed to query.
 *
 * Backed by `GET /api/search/meta` so the role → labels mapping lives in
 * one place (`config/search.php`) instead of being mirrored on the FE.
 * Cached aggressively — the result only changes when the user switches
 * role/workspace, both of which are part of the auth state so the query
 * key picks them up automatically.
 */
export const useSearchMeta = () => {
  const role = useUserStore((s) => s.role);
  const workspaceId = useUserStore((s) => s.selectedWorkspaceId);
  const isAuthed = !!useUserStore((s) => s.token);

  return useQuery<SearchMetaResponse>({
    queryKey: ["search-meta", role, workspaceId],
    queryFn: () => fetcher<SearchMetaResponse>("/search/meta"),
    enabled: isAuthed,
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

/**
 * Build the placeholder shown in the Topbar search input from the meta
 * response. Keeps it short — first three labels, lowercased, joined with
 * commas, with a trailing ellipsis if there are more.
 *
 * Falls back to a generic prompt so the input never shows "undefined"
 * while the meta request is still in flight.
 */
export const buildSearchPlaceholder = (items?: SearchMetaItem[]): string => {
  if (!items || items.length === 0) {
    return "Search…";
  }
  const visible = items.slice(0, 3).map((i) => i.label.toLowerCase());
  const more = items.length > 3;
  return `Search ${visible.join(", ")}${more ? ", …" : "…"}`;
};
